import React from 'react';
import { Card, Row, Col, Statistic, Spin, Alert } from 'antd';
import { 
  EyeOutlined, 
  CameraOutlined, 
  CheckCircleOutlined, 
  DownloadOutlined,
  FileZipOutlined
} from '@ant-design/icons';
import { useGetEventAnalyticsQuery } from '../services/nestjs-base-api';
import { useAuth } from '../hooks/useAuth';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 20px !important;
  }
`;

const StatisticCard = styled(Card)`
  height: 120px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .ant-card-body {
    padding: 16px !important;
  }
  
  .ant-statistic {
    text-align: center !important;
  }
  
  .ant-statistic-title {
    color: #666 !important;
    font-size: 14px !important;
    margin-bottom: 8px !important;
  }
  
  .ant-statistic-content {
    color: #1890ff !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  .anticon {
    color: #1890ff !important;
    margin-bottom: 8px !important;
    font-size: 20px !important;
    display: block !important;
  }
`;

const LoadingWrapper = styled.div`
  text-align: center;
  padding: 40px 0;
`;

interface EventAnalyticsProps {
  eventId: number;
}

const EventAnalytics: React.FC<EventAnalyticsProps> = ({ eventId }) => {
  const intl = useIntl();
  const { user } = useAuth();
  
  const { 
    data: analytics, 
    error, 
    isLoading 
  } = useGetEventAnalyticsQuery({
    jwtToken: user.jwtToken,
    eventId
  });

  if (isLoading) {
    return (
      <StyledCard>
        <LoadingWrapper>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>
            {intl.formatMessage({ id: 'analytics.loading' }) || 'Loading analytics...'}
          </div>
        </LoadingWrapper>
      </StyledCard>
    );
  }

  if (error) {
    return (
      <StyledCard>
        <Alert
          message={intl.formatMessage({ id: 'analytics.error' }) || 'Error loading analytics'}
          description={intl.formatMessage({ id: 'analytics.errorDesc' }) || 'Unable to load event analytics data. Please try again later.'}
          type="error"
          showIcon
        />
      </StyledCard>
    );
  }

  if (!analytics) {
    return (
      <StyledCard>
        <Alert
          message={intl.formatMessage({ id: 'analytics.noData' }) || 'No analytics data'}
          description={intl.formatMessage({ id: 'analytics.noDataDesc' }) || 'No analytics data available for this event yet.'}
          type="info"
          showIcon
        />
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={4.8}>
          <StatisticCard>
            <div style={{ textAlign: 'center' }}>
              <EyeOutlined />
              <Statistic
                title={intl.formatMessage({ id: 'analytics.eventViews' }) || 'Event Views'}
                value={analytics.eventViews}
                precision={0}
              />
            </div>
          </StatisticCard>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={4.8}>
          <StatisticCard>
            <div style={{ textAlign: 'center' }}>
              <CameraOutlined />
              <Statistic
                title={intl.formatMessage({ id: 'analytics.selfieUploads' }) || 'Selfie Uploads'}
                value={analytics.selfieUploads}
                precision={0}
              />
            </div>
          </StatisticCard>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={4.8}>
          <StatisticCard>
            <div style={{ textAlign: 'center' }}>
              <CheckCircleOutlined />
              <Statistic
                title={intl.formatMessage({ id: 'analytics.selfieMatches' }) || 'Successful Matches'}
                value={analytics.selfieMatches}
                precision={0}
              />
            </div>
          </StatisticCard>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={4.8}>
          <StatisticCard>
            <div style={{ textAlign: 'center' }}>
              <DownloadOutlined />
              <Statistic
                title={intl.formatMessage({ id: 'analytics.photoDownloads' }) || 'Photo Downloads'}
                value={analytics.photoDownloads}
                precision={0}
              />
            </div>
          </StatisticCard>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={4.8}>
          <StatisticCard>
            <div style={{ textAlign: 'center' }}>
              <FileZipOutlined />
              <Statistic
                title={intl.formatMessage({ id: 'analytics.bulkDownloads' }) || 'Bulk Downloads'}
                value={analytics.bulkDownloads}
                precision={0}
              />
            </div>
          </StatisticCard>
        </Col>
      </Row>
    </StyledCard>
  );
};

export default EventAnalytics;